/**
 * Pure color math for the theme editor.
 *
 * The app stores colors as shadcn-style HSL triplet strings ("228 29% 14%",
 * no hsl() wrapper). This module parses/formats those, converts to/from hex
 * (for <input type="color"> and the reference presets), and provides the
 * lightness/saturation/mix and WCAG-contrast primitives the token derivation
 * relies on.
 */

export interface Hsl {
  /** hue, 0–360 */
  h: number
  /** saturation, 0–100 */
  s: number
  /** lightness, 0–100 */
  l: number
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))
const clampHue = (h: number): number => ((h % 360) + 360) % 360

/** Parse a shadcn HSL token string like "228 29% 14%" into an {h,s,l}. */
export function parseHslToken(token: string): Hsl {
  const m = token.trim().match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/)
  if (!m) {
    throw new Error(`Invalid HSL token: "${token}" (expected e.g. "228 29% 14%")`)
  }
  return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) }
}

/** Format an {h,s,l} back into a shadcn HSL token string with rounded channels. */
export function formatHslToken({ h, s, l }: Hsl): string {
  return `${Math.round(clampHue(h))} ${Math.round(clamp(s, 0, 100))}% ${Math.round(clamp(l, 0, 100))}%`
}

/** CSS-ready `hsl(...)` string. */
export function hslCss({ h, s, l }: Hsl): string {
  return `hsl(${Math.round(clampHue(h))} ${Math.round(clamp(s, 0, 100))}% ${Math.round(clamp(l, 0, 100))}%)`
}

/** Convert a hex string ("#00b41f", "00b41f", "#FFF"-style 6-digit) to {h,s,l}. */
export function hexToHsl(hex: string): Hsl {
  const clean = hex.replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    throw new Error(`Invalid hex color: "${hex}"`)
  }
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6
        break
      case g:
        h = (b - r) / delta + 2
        break
      default:
        h = (r - g) / delta + 4
    }
    h *= 60
  }

  return {
    h: Math.round(clampHue(h)),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

/** Convert {h,s,l} to a 6-digit lowercase hex string with leading #. */
export function hslToHex({ h, s, l }: Hsl): string {
  const hue = clampHue(h)
  const sat = clamp(s, 0, 100) / 100
  const light = clamp(l, 0, 100) / 100

  const c = (1 - Math.abs(2 * light - 1)) * sat
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = light - c / 2

  let rp = 0
  let gp = 0
  let bp = 0
  if (hue < 60) [rp, gp, bp] = [c, x, 0]
  else if (hue < 120) [rp, gp, bp] = [x, c, 0]
  else if (hue < 180) [rp, gp, bp] = [0, c, x]
  else if (hue < 240) [rp, gp, bp] = [0, x, c]
  else if (hue < 300) [rp, gp, bp] = [x, 0, c]
  else [rp, gp, bp] = [c, 0, x]

  const toHex = (v: number): string =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(rp)}${toHex(gp)}${toHex(bp)}`
}

/** Return a copy with lightness shifted by `delta` (clamped to [0,100]). */
export function adjustLightness(hsl: Hsl, delta: number): Hsl {
  return { ...hsl, l: clamp(hsl.l + delta, 0, 100) }
}

/** Return a copy with saturation shifted by `delta` (clamped to [0,100]). */
export function adjustSaturation(hsl: Hsl, delta: number): Hsl {
  return { ...hsl, s: clamp(hsl.s + delta, 0, 100) }
}

/**
 * Linearly interpolate between two colors. `weight` is how much of `b`:
 * 0 → a, 1 → b. Hue is mixed along the shortest arc.
 */
export function mix(a: Hsl, b: Hsl, weight: number): Hsl {
  const w = clamp(weight, 0, 1)
  const dh = ((b.h - a.h + 540) % 360) - 180 // shortest signed arc
  return {
    h: clampHue(a.h + dh * w),
    s: a.s + (b.s - a.s) * w,
    l: a.l + (b.l - a.l) * w,
  }
}

const channelLuminance = (c: number): number => {
  const cs = c / 255
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4)
}

/** WCAG relative luminance (0–1) of an HSL color. */
export function relativeLuminance(hsl: Hsl): number {
  const hex = hslToHex(hsl).slice(1)
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
}

/** WCAG contrast ratio between two colors (1–21). */
export function contrastRatio(a: Hsl, b: Hsl): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

/** Of two candidate foregrounds, return whichever has higher contrast on `bg`. */
export function pickReadableForeground(bg: Hsl, optionA: Hsl, optionB: Hsl): Hsl {
  return contrastRatio(bg, optionA) >= contrastRatio(bg, optionB) ? optionA : optionB
}
